import { Store } from '@ngrx/store';
import moment from 'moment';
import { firstValueFrom, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';

import * as actions from 'app/modules/social/state/app/actions';
import { AppModuleServices } from 'app/utils/app-module-util';
import { Loggable } from 'app/utils/logging/loggable';
import { notUndefined, resettableSubject } from 'app/utils/stream-util';
import {
  IncomingMessageType,
  StateChangeErrorMessage,
  StateNotChangedMessage,
  SocialState,
  SocialEvent,
  SocialEventType,
  Toast,
  Convo,
  ConvoPhase,
  Presentation,
  Presenter,
  BaseUser,
  ScreenShare,
  ScreenShareStatus,
  MapAddMessage,
  TypedOutgoingMessage,
  OutgoingMessageType,
  ArrayRemoveMessage,
  ArrayAddUniqueMessage,
  PropertyUncheckedStateChangeMessage,
  PropertyCheckedStateChangeMessage,
  MapRemoveMessage,
  UpdateType,
  InitialStateMessage,
  StateChangeMessage,
  IncomingMessage,
  StateChangeAckMessage,
  ArrayMultiAddUniqueMessage,
} from 'messages/websocket.messages';

import { WebsocketService } from '../services/websocket.service';

import SocialStateHelper from './social-state-helper';
import { InteractionModePresets } from './social-state.constants';

const STATE_CHANGE_TIMEOUT_MS = 10 * 1000;

type StateChangeMessageType =
  | PropertyCheckedStateChangeMessage
  | PropertyUncheckedStateChangeMessage
  | ArrayAddUniqueMessage
  | ArrayRemoveMessage
  | ArrayMultiAddUniqueMessage
  | MapAddMessage
  | MapRemoveMessage;

export default class SocialStateMachine extends Loggable {
  public static shared = new SocialStateMachine();

  private websocketService: WebsocketService;
  private _state?: SocialState;
  public get state(): SocialState | undefined {
    return this._state;
  }

  private stateChanged = resettableSubject(() => new ReplaySubject<SocialState>(1));
  public requestMade$ = new ReplaySubject<TypedOutgoingMessage>(Infinity);
  public requestPending$ = new Subject<boolean>();

  public constructor() {
    super();
    this.setDisplayName('SocialStateMachine');
    this.websocketService = AppModuleServices.injector.get<WebsocketService>(WebsocketService);
  }

  /**
   * Reset social state, removing any exiting state.
   **/
  public reset() {
    this.stateChanged.reset();
  }

  /**
   * An observable that emits when the state changes.
   **/
  public get stateChanged$(): Observable<SocialState> {
    return this.stateChanged.observable;
  }

  /**   Handle updated state (type: 'state-change') from websocket. */
  public onReceiveState(store: Store, json: StateChangeMessage | InitialStateMessage): void {
    let region;
    const { state: newState } = json;

    if (json.t === IncomingMessageType.INIT_STATE) {
      region = json.region;
    }

    if (!newState) {
      return;
    }
    this.requestPending$.next(false);
    const prevState = this._state;
    this._state = newState;
    const previousEvent = prevState?.eventType ?? SocialEventType.NONE;
    const socialEvent = newState.eventType === SocialEventType.NEVER ? previousEvent : SocialEventType.NONE;

    store.dispatch(actions.RECEIVE_SHARED_STATE({ sharedState: newState, socialEvent, region }));

    this.stateChanged.subject.next(newState);
    return;
  }

  /** State not changed means the state change request assumed something about the state
   * that wasn't true, e.g. starting a presentation when a presentation was already in progress.
   * Eventually we may want to provide better input to the user when this happens.
   **/
  public onStateNotChanged(_msg: StateNotChangedMessage): void {
    this.requestPending$.next(false);
  }

  public onStateChangeError(msg: StateChangeErrorMessage): void {
    this.requestPending$.next(false);
    this.error(`State Change Request Error`, { msg });
  }

  /**
   * STATE CHANGE REQUESTS
   **/

  /**  Make a request to update Social State to add a host */
  public requestAddHost({ hostId, userId }: { hostId: string; userId: string }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: ArrayAddUniqueMessage = {
      t: OutgoingMessageType.ARRAY_ADD_UNIQUE,
      name: 'hosts',
      value: hostId,
      tx: `${userId}-addHost-${hostId}-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to remove a host */
  public requestRemoveHost({ hostId, userId }: { hostId: string; userId: string }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: ArrayRemoveMessage = {
      t: OutgoingMessageType.ARRAY_REMOVE,
      name: 'hosts',
      value: hostId,
      tx: `${userId}-removeHost-${hostId}-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  Make a request to update Social State to end an event, checking to ensure we're ending the expected event type.
   *  Update All Rooms = ALL
   *  Update Current Room = CURRENT
   *  Update Single to Cross = OTHERS
   *  Update Cross to Single = OTHERS
   **/
  public requestEndEvent({
    prevEventType,
    updateType = UpdateType.CURRENT,
    userId,
  }: {
    prevEventType: SocialEventType;
    userId: string;
    updateType?: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'eventType',
      value: SocialEventType.NONE,
      tx: `${userId}-End-${prevEventType}-${timestamp}`,
      changes: {
        socialEvent: {},
      },
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   * Request start new event private method utility
   * Make a request to update Social State with any new event, checking to ensure there's no existing event first.
   **/
  public _requestStartNewEvent({
    eventType,
    socialEvent,
    updateType = UpdateType.CURRENT,
    userId,
  }: {
    eventType: SocialEventType;
    socialEvent: SocialEvent;
    userId: string;
    updateType?: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const tx = `${userId}-${eventType}-${timestamp}`;

    const request: PropertyCheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_CHECKED,
      name: 'eventType',
      value: eventType,
      tx,
      updateType,
      check: SocialEventType.NONE,
      changes: { socialEvent },
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to start a TOAST, checking to ensure there's no other social event in progress. */
  public requestStartNewToast({
    message,
    presenter,
    userId,
  }: Partial<Toast> & { userId: string; message: string; presenter: Presenter }): Promise<StateChangeAckMessage> {
    const startTime = moment.utc().add(7, 's').toISOString();

    return this._requestStartNewEvent({
      userId,
      eventType: SocialEventType.TOAST,
      socialEvent: {
        startTime,
        presenter,
        message,
        eventType: SocialEventType.TOAST,
        toastIsActive: false,
      },
    });
  }

  public requestUpdateToastIsActive({
    toastIsActive,
    userId,
  }: {
    toastIsActive: boolean;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'socialEvent.toastIsActive',
      value: toastIsActive,
      tx: `${userId}-toastIsActive-${toastIsActive}-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to start a CONVO, checking to ensure there's no other social event in progress. */
  public requestStartNewConvo({
    conversation,
    convoMessage,
    presenter,
    userId,
  }: Pick<Convo, 'presenter' | 'convoMessage' | 'conversation'> & { userId: string }): Promise<StateChangeAckMessage> {
    return this._requestStartNewEvent({
      userId,
      eventType: SocialEventType.CONVO,
      socialEvent: {
        presenter,
        phase: ConvoPhase.ANNOUNCE_PHASE,
        eventType: SocialEventType.CONVO,
        convoMessage,
        conversation,
      },
    });
  }

  /**  Make a request to update Social State to start the BANNER phase of a CONVO */
  public requestConvoToBannerPhase({ userId }: { userId: string }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: PropertyCheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_CHECKED,
      name: 'socialEvent.phase',
      check: ConvoPhase.ANNOUNCE_PHASE,
      value: ConvoPhase.BANNER_PHASE,
      tx: `${userId}-convo-phase-banner-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  PRESENTATION
   *  Update All Rooms = ALL
   *  Update Current Room = CURRENT
   *  Update Single to Cross = OTHERS
   *  Update Cross to Single = OTHERS
   **/

  public requestStartNewPresentation({
    crossRoom = true,
    presentationCreator = 'unknown',
    presenters = {},
    queuedToSpeakIds = [],
    requestedPresenterIds = [],
    settings = InteractionModePresets.DEFAULT_SMALL_ROOM_PRESET,
    speakingParticipants = {},
    updateType = UpdateType.CURRENT,
    userId,
  }: Partial<Presentation> & { userId: string; updateType?: UpdateType }): Promise<StateChangeAckMessage> {
    const eventType = SocialEventType.PRESENTATION;
    const socialEvent: Presentation = {
      requestedPresenterIds,
      settings,
      presenters,
      eventType,
      presentationCreator,
      queuedToSpeakIds,
      speakingParticipants,
      crossRoom,
    };

    const timestamp = Date.now();
    const tx = `${userId}-${eventType}-${timestamp}`;

    const request: PropertyCheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_CHECKED,
      name: 'eventType',
      value: eventType,
      tx,
      updateType,
      check: SocialEventType.NONE,
      changes: { socialEvent },
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   * Make a request to update Social State to start a Cross presentation from local
   */
  public requestStartCrossPresentation({
    crossRoom = true,
    presentationCreator = 'unknown',
    presenters = {},
    queuedToSpeakIds = [],
    requestedPresenterIds = [],
    settings = InteractionModePresets.DEFAULT_SMALL_ROOM_PRESET,
    speakingParticipants = {},
    updateType = UpdateType.OTHERS,
    userId,
  }: Partial<Presentation> & { userId: string; updateType?: UpdateType }): Promise<StateChangeAckMessage> {
    const eventType = SocialEventType.PRESENTATION;
    const socialEvent: Presentation = {
      requestedPresenterIds,
      settings,
      presenters,
      eventType,
      presentationCreator,
      queuedToSpeakIds,
      speakingParticipants,
      crossRoom,
    };

    const timestamp = Date.now();
    const tx = `${userId}-${eventType}-${timestamp}`;

    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'eventType',
      value: eventType,
      tx,
      updateType,
      changes: { socialEvent },
    };
    return this.sendStateChangeRequest(request);
  }

  public requestPlayMediaEmbed({
    playing,
    playtingTime,
    presenter,
    updateType,
    userId,
  }: {
    playing: boolean;
    playtingTime: number;
    presenter: Presenter;
    updateType: UpdateType;
    userId: string;
  }) {
    const timestamp = Date.now();
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: `socialEvent.presenters.${presenter.userId}.media.playing`,
      value: playing,
      changes: {
        [`socialEvent.presenters.${presenter.userId}.media.playtingTime`]: playtingTime,
        [`socialEvent.presenters.${presenter.userId}.media.startTime`]: timestamp,
      },
      tx: `${userId}-updatePresenter-${presenter.userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestUpdatePresenter({
    presenter,
    updateType,
    userId,
  }: {
    presenter: Presenter;
    updateType: UpdateType;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapAddMessage = {
      t: OutgoingMessageType.MAP_ADD,
      name: 'socialEvent.presenters',
      key: presenter.userId,
      value: presenter,
      tx: `${userId}-updatePresenter-${presenter.userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   * Make a request to update Social State to add and remove active and invited participants.
   * @param  activePresenters,
   * @param  idsToRemove,
   * @param  presenterIdsToInvite,
   * @param  userId,
   */
  public async requestUpdatePresenters({
    activePresenters,
    idsToRemove,
    presenterIdsToInvite,
    updateType,
    userId,
  }: {
    idsToRemove?: string[];
    activePresenters?: Presenter[];
    presenterIdsToInvite?: string[];
    userId: string;
    updateType: UpdateType;
  }) {
    const { state } = this;
    if (!state) {
      this.error('requestUpdatePresenters called with no state');
      return;
    }
    // TODO: rework this method to use the bulk methods
    const invitesToAdd: string[] = [];

    // Invited Presenters to Add
    presenterIdsToInvite?.forEach((selectedPresenterId) => {
      if (
        !SocialStateHelper.isUserPresenter(state, selectedPresenterId) &&
        !SocialStateHelper.isUserInvited(state, selectedPresenterId)
      ) {
        invitesToAdd.push(selectedPresenterId);
      }
    });

    // Current Active Presenters
    activePresenters?.forEach(async (presenter) => {
      await this.requestAddActivePresenter({ presenter, userId, updateType }).catch((e) => this.error(e));
    });
    await this.requestInvitePresenters({ presenterIds: invitesToAdd, userId });

    // Presenters to remove
    idsToRemove?.forEach(async (id) => {
      if (SocialStateHelper.isUserPresenter(state, id)) {
        await this.requestRemoveActivePresenter({ presenterId: id, userId, updateType }).catch((e) => this.error(e));
        return;
      }
      if (SocialStateHelper.isUserInvited(state, id)) {
        await this.requestRejectPresenterInvite({ presenter: { userId: id }, userId }).catch((e) => this.error(e));
      }
    });
  }

  public requestUpdatePresentation({
    operation,
    updateType = UpdateType.CURRENT,
    updatedProperties,
    userId,
  }: {
    operation: string;
    updatedProperties: Partial<Presentation>;
    userId: string;
    updateType?: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const { state } = this;
    if (!state) {
      const error = 'requestUpdatePresentation called with no state';
      this.error(error);
      throw new Error(error);
    }
    const newPresentation = SocialStateHelper.getNewPresentationState({
      state,
      updatedProperties,
    });
    const timestamp = Date.now();
    const request: PropertyCheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_CHECKED,
      name: 'eventType',
      check: SocialEventType.PRESENTATION,
      value: SocialEventType.PRESENTATION,
      changes: { socialEvent: newPresentation },
      tx: `${userId}-updateSettings-${operation}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /** Request End Presentation **/
  public requestEndPresentation({
    updateType = UpdateType.CURRENT,
    userId,
  }: {
    userId: string;
    updateType?: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'eventType',
      value: SocialEventType.NONE,
      tx: `${userId}-End-Presentation-${timestamp}`,
      changes: {
        socialEvent: {},
      },
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to invite a presenter to a presentation in progress. */
  public requestInvitePresenters({
    presenterIds,
    userId,
  }: {
    presenterIds: string[];
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const idsTx = presenterIds.join('_');
    const request: ArrayMultiAddUniqueMessage = {
      t: OutgoingMessageType.ARRAY_MULTI_ADD_UNIQUE,
      name: 'socialEvent.requestedPresenterIds',
      values: presenterIds,
      tx: `${userId}-invitePresenter-${idsTx}-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  Make a request to update Social State to add a presenter who has accepted an invite to invited presenters.
   *
   * @param crossRoom: boolean, if true modify state across all rooms in the set.
   * @param presenter: The presenter to add to the presentation.
   * @param userId: The userId of the user making the request.
   *  */
  public requestAddActivePresenter({
    presenter,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    presenter: Presenter;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapAddMessage = {
      t: OutgoingMessageType.MAP_ADD,
      checkProperty: '<',
      check: 4,
      name: 'socialEvent.presenters',
      key: presenter.userId,
      value: presenter,
      tx: `${userId}-addPresenter-${presenter.userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestAddActivePresentation({ socialId }: { socialId: string }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapAddMessage = {
      t: OutgoingMessageType.MAP_ADD,
      name: 'presentations.map',
      key: socialId,
      value: 'true',
      tx: `${socialId}-addPresentation-${timestamp}`,
      updateType: UpdateType.ALL,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestRemoveActivePresentation({ socialId }: { socialId: string }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapRemoveMessage = {
      t: OutgoingMessageType.MAP_REMOVE,
      name: 'presentations.map',
      key: socialId,
      // value: true,
      tx: `${socialId}-addPresentation-${timestamp}`,
      updateType: UpdateType.ALL,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to remove a presenter who has not accepted an invite from invited presenters. */
  public requestRejectPresenterInvite({
    presenter,
    userId,
  }: {
    presenter: { userId: string };
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: ArrayRemoveMessage = {
      t: OutgoingMessageType.ARRAY_REMOVE,
      name: 'socialEvent.requestedPresenterIds',
      value: presenter.userId,
      tx: `${userId}-rejectPresenter-${presenter.userId}-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to remove a presenter who has accepted an invite from invited presenters. */
  public requestAcceptPresenterInvite({
    presenter,
    userId,
  }: {
    presenter: Presenter;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: ArrayRemoveMessage = {
      t: OutgoingMessageType.ARRAY_REMOVE,
      name: 'socialEvent.requestedPresenterIds',
      value: presenter.userId,
      tx: `${userId}-acceptPresenter-${presenter.userId}-${timestamp}`,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   * Make a request to update Social State to end a current presenter's presentation.
   * @param updateType: Where to update the state: current room, other rooms or all rooms.
   * @param presenterId: the presenter to remove
   * @param userId: The userId of the user making the request.
   *  */
  public requestRemoveActivePresenter({
    presenterId,
    updateType,
    userId,
  }: {
    presenterId: string;
    userId: string;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapRemoveMessage = {
      t: OutgoingMessageType.MAP_REMOVE,
      name: 'socialEvent.presenters',
      key: presenterId,
      tx: `${userId}-removePresenter-${presenterId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestRemoveActiveScreenshares({
    presentationType,
    updateType,
  }: {
    updateType: UpdateType;
    presentationType: string;
  }): Promise<StateChangeAckMessage> {
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: presentationType === 'presentation' ? 'presentations' : 'screenShareStatus',
      value: 'NONE',
      tx: 'fix-tx',
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to allow a participant to speak to the group. */
  public requestAddParticipantToSpeaking({
    participant,
    slots,
    updateType,
    userId,
  }: {
    participant: BaseUser;
    userId: string;
    updateType: UpdateType;
    slots?: number;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapAddMessage = {
      t: OutgoingMessageType.MAP_ADD,
      name: 'socialEvent.speakingParticipants',
      checkProperty: slots ? '<' : undefined,
      check: slots,
      key: participant.userId,
      value: participant,
      tx: `${userId}-addSpeakingParticipant-${participant.userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to remove a participant from speaking participants. */
  public requestRemoveSpeakingParticipant({
    participantId,
    updateType,
    userId,
  }: {
    participantId: string;
    userId: string;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapRemoveMessage = {
      t: OutgoingMessageType.MAP_REMOVE,
      name: 'socialEvent.speakingParticipants',
      key: participantId,
      tx: `${userId}-removeSpeakingParticipant-${participantId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to add a participant to the speaking queue (Q&A only) */
  public requestAddParticipantToSpeakingQueue({
    participant,
    updateType,
    userId,
  }: {
    participant: BaseUser;
    userId: string;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: ArrayAddUniqueMessage = {
      t: OutgoingMessageType.ARRAY_ADD_UNIQUE,
      name: 'socialEvent.queuedToSpeakIds',
      value: participant.userId,
      tx: `${userId}-addParticipantToSpeakingQueue-${participant.userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**  Make a request to update Social State to remove a participant from the speaking queue (Q&A only) */
  public requestRemoveParticipantFromSpeakingQueue({
    participant,
    updateType,
    userId,
  }: {
    participant: BaseUser;
    userId: string;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: ArrayRemoveMessage = {
      t: OutgoingMessageType.ARRAY_REMOVE,
      name: 'socialEvent.queuedToSpeakIds',
      value: participant.userId,
      tx: `${userId}-removeParticipantFromSpeakingQueue-${participant.userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  LIVESTREAM
   *  Update All Rooms = ALL
   *  Update Current Room = CURRENT
   *  Update Single to Cross = OTHERS
   *  Update Cross to Single = OTHERS
   **/
  public requestAddLivestreamToQueue({
    livestream,
    updateType,
  }: {
    livestream: Presenter;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const { userId: streamId } = livestream;
    const request: MapAddMessage = {
      t: OutgoingMessageType.MAP_ADD,
      name: 'livestreams.queuedLivestreams',
      key: streamId,
      value: livestream,
      tx: `addLivestreamToQueue-${streamId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestRemoveLivestreamFromQueue({
    streamId,
    updateType,
  }: {
    streamId: string;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapRemoveMessage = {
      t: OutgoingMessageType.MAP_REMOVE,
      name: 'livestreams.queuedLivestreams',
      key: streamId,
      tx: `removeLivestreamFromQueue-${streamId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  Make a request to update Social State to begin a screen share.
   *  Note that this should currently only happen while a PRESENTATION is in progress, e.g. eventType === PRESENTATION .
   *
   * @param crossRoom: boolean, if true modify state across all rooms in the set.
   * @param screenShare: The screen share configuration
   * @param userId: The userId of the user making the request.
   * */
  public requestStartScreenShare({
    screenShare,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    screenShare: ScreenShare;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const tx = `${userId}-startScreenShare-${screenShare.presenter?.userId}-${timestamp}`;
    const request: PropertyCheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_CHECKED,
      check: ScreenShareStatus.NONE,
      name: 'screenShareStatus',
      value: ScreenShareStatus.ACTIVE,
      changes: { screenShare },
      tx,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestUpdateScreenShare({
    screenShare,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    screenShare: ScreenShare;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const tx = `${userId}-startScreenShare-${screenShare.presenter?.userId}-${timestamp}`;
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'screenShareStatus',
      value: ScreenShareStatus.ACTIVE,
      changes: { screenShare },
      tx,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  Make a request to update Social State to end a screen share.
   *
   * @param crossRoom: boolean, if true modify state across all rooms in the set.
   * @param presenterId The userId of the current presenter.
   * @param userId: The userId of the user making the request.
   * */
  public requestEndScreenShare({
    presenterId,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    presenterId: string;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const tx = `${userId}-endScreenShare-${presenterId}-${timestamp}`;
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'screenShareStatus',
      value: ScreenShareStatus.NONE,
      changes: { screenShare: {} },
      tx,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   *  GROUP SCREEN SHARES
   *  UpdateType.CURRENT --> Will always be set only in local room
   */
  public requestAddGroupScreenShare({
    screenShare,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    screenShare: ScreenShare;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapAddMessage = {
      t: OutgoingMessageType.MAP_ADD,
      name: 'groupScreenShares.map',
      key: userId,
      value: screenShare,
      tx: `addGroupScreenShare-${userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestAddGroupScreenParticipant({
    groupShareId,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    groupShareId: string;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const request: ArrayAddUniqueMessage = {
      t: OutgoingMessageType.ARRAY_ADD_UNIQUE,
      name: `groupScreenShares.map.${groupShareId}.viewerUserIds`,
      value: userId,
      tx: `x-value`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestRemoveGroupScreenParticipant({
    groupShareId,
    updateType,
    userId,
  }: {
    updateType: UpdateType;
    groupShareId: string;
    userId: string;
  }): Promise<StateChangeAckMessage> {
    const request: ArrayRemoveMessage = {
      t: OutgoingMessageType.ARRAY_REMOVE,
      name: `groupScreenShares.map.${groupShareId}.viewerUserIds`,
      value: userId,
      tx: `x-value`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestUpdateGroupScreenShareRecording({
    groupShareId,
    updateType,
    updatedProperties,
  }: {
    updateType: UpdateType;
    updatedProperties: Partial<ScreenShare>;
    groupShareId: string;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();

    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: `groupScreenShares.map.${groupShareId}.recordingId`,
      value: updatedProperties.recordingId,
      tx: `updateGroupScreenShare-${groupShareId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  public requestRemoveGroupScreenShare({
    updateType,
    userId,
  }: {
    userId: string;
    updateType: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    const request: MapRemoveMessage = {
      t: OutgoingMessageType.MAP_REMOVE,
      name: 'groupScreenShares.map',
      key: userId,
      tx: `removeGroupScreenShare-${userId}-${timestamp}`,
      updateType,
    };
    return this.sendStateChangeRequest(request);
  }

  /**
   * Force all rooms in the connected room set to event type NEVER.
   *
   * NEVER enforces that a room will not begin a new event, and will tear down the current event.
   *
   * @param param0 { userId: string } The user performing the state modification.
   * @returns A promise that resolves when the state change request is complete.
   */
  public restrictCrossRoomEvents({
    updateType = UpdateType.ALL,
    userId,
  }: {
    userId: string;
    updateType?: UpdateType;
  }): Promise<StateChangeAckMessage> {
    const timestamp = Date.now();
    // Force all rooms into NEVER to prevent state changes
    const request: PropertyUncheckedStateChangeMessage = {
      t: OutgoingMessageType.PROPERTY_UNCHECKED,
      name: 'eventType',
      value: SocialEventType.NEVER,
      tx: `${userId}-PreventEvents-${timestamp}`,
      changes: {
        socialEvent: {},
      },
      updateType,
    };

    return this.sendStateChangeRequest(request);
  }

  /**
   * Send acknowledged state change message to the server, and wait for the result.
   *
   * This will throw if the state change fails.
   * @param msg State change message
   * @returns The state change acknowledgement message.
   */
  private async sendStateChangeRequest(msg: StateChangeMessageType): Promise<StateChangeAckMessage> {
    const result$ = this.websocketService.message$.pipe(
      map((response: IncomingMessage): StateChangeErrorMessage | StateChangeAckMessage | undefined => {
        switch (response.t) {
          case IncomingMessageType.STATE_CHANGE_ACK:
          case IncomingMessageType.STATE_CHANGE_ERROR: {
            if (response.tx === msg.tx) {
              return response;
            }
            break;
          }
          default:
            return undefined;
        }
      }),
      filter(notUndefined),
      timeout({
        each: STATE_CHANGE_TIMEOUT_MS,
        with: () =>
          throwError(
            () => new Error(`Timeout waiting for state change response, not received in ${STATE_CHANGE_TIMEOUT_MS}ms`),
          ),
      }),
      take(1),
    );

    this.websocketService.sendMessage(msg);
    this.requestPending$.next(false);
    this.info('Sent state change request through websocket', { msg });

    const result = await firstValueFrom(result$);
    switch (result.t) {
      case IncomingMessageType.STATE_CHANGE_ACK:
        return result;
      case IncomingMessageType.STATE_CHANGE_ERROR:
        throw new Error(`State change request failed: ${result.error}`);
      default:
        throw new Error(`State change request unexpected response: ${result}`);
    }
  }
}
